import {useCallback, useEffect} from 'react';
import {ClientAnalytics} from '@shopify/hydrogen';
import {ANALYTIC_EVENT_NAMES} from '~/lib/const';
import {hashCode} from '~/lib';

let init = false;
let lastCanonicalPath = '';

const LOCAL_STORAGE_EVENT_PREFIX = 'GoogleAnalytics-';

function sendLocalStorageEvents() {
  for (let index = 0; index < localStorage.length; index++) {
    if (localStorage.key(index)?.startsWith(LOCAL_STORAGE_EVENT_PREFIX)) {
      // @ts-ignore
      gtag(JSON.parse(localStorage.getItem(localStorage.key(index))!));

      localStorage.removeItem(localStorage.key(index)!);
    }
  }
}

export function GoogleAnalytics() {
  const queueEvents = useCallback((payload: any) => {
    if (lastCanonicalPath === payload?.shopify?.canonicalPath) {
      if (payload.events) {
        for (const event of payload.events) {
          // @ts-ignore
          gtag(event);
        }
      } else {
        // @ts-ignore
        gtag(payload.event);
      }

      return;
    }

    // We save the events in a queue to be sent after the first page view
    if (payload && payload.event) {
      const event = JSON.stringify(payload.event);

      // We use the hash code to avoid saving the same event multiple times
      // We're saving in local storage because the events are lost when
      // multiple events arrives in the same time
      localStorage.setItem(
        `${LOCAL_STORAGE_EVENT_PREFIX}${hashCode(event)}`,
        event,
      );
    } else if (payload.events) {
      for (const payloadEvent of payload.events) {
        const event = JSON.stringify(payloadEvent);
        localStorage.setItem(
          `${LOCAL_STORAGE_EVENT_PREFIX}${hashCode(event)}`,
          event,
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!init) {
      init = true;

      // eslint-disable-next-line no-inner-declarations
      function trackPageView(payload: any) {
        // @ts-ignore
        gtag({
          _clear: true,
          event: ANALYTIC_EVENT_NAMES.PAGE_DETAILS_LOADED,
          page: {
            canonical_path: payload?.shopify?.canonicalPath ?? '',
            login_status: payload?.shopify?.isLoggedIn
              ? 'logged in'
              : 'logged out',
          },
        });

        lastCanonicalPath = payload?.shopify?.canonicalPath ?? '';

        // We send the events that were saved in the queue
        sendLocalStorageEvents();
      }

      // Listen for events from Hydrogen
      ClientAnalytics.subscribe(
        ClientAnalytics.eventNames.PAGE_VIEW,
        trackPageView,
      );

      // We need to garantee that the first page view is sent
      // before any other event
      ClientAnalytics.subscribe(ANALYTIC_EVENT_NAMES.EMAIL_SIGNUP, queueEvents);
      ClientAnalytics.subscribe(
        ANALYTIC_EVENT_NAMES.VIEW_SEARCH_RESULTS,
        queueEvents,
      );
      ClientAnalytics.subscribe(ANALYTIC_EVENT_NAMES.ADD_TO_CART, queueEvents);
      ClientAnalytics.subscribe(ANALYTIC_EVENT_NAMES.VIEW_CART, queueEvents);
      ClientAnalytics.subscribe(ANALYTIC_EVENT_NAMES.VIEW_ITEM, queueEvents);
      ClientAnalytics.subscribe(
        ANALYTIC_EVENT_NAMES.VIEW_ITEM_LIST,
        queueEvents,
      );
      ClientAnalytics.subscribe(
        ANALYTIC_EVENT_NAMES.NAVIGATION_CLICK,
        queueEvents,
      );

      ClientAnalytics.hasSentFirstPageView() &&
        trackPageView(ClientAnalytics.getPageAnalyticsData());
    }
  });

  return null;
}
